import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsEvent, InternalAnalyticsService } from '../../core/internal-analytics.service';
import { SubscriptionService } from '../../core/subscription/subscription.service';

@Component({
    selector: 'rosie-billing-alert',
    templateUrl: './billing-alert.component.html',
    styleUrls: ['./billing-alert.component.scss'],
    standalone: false
})
export class BillingAlertComponent {
    isMobileVersion: boolean = false;
    section: string = BillingAlertSection.Sidebar;

    constructor(private subscription: SubscriptionService, private analytics: InternalAnalyticsService, private router: Router) {
    }

    get icon() {
        if (this.subscription.isPendingApproval) {
            return 'heroExclamationTriangleSolid';
        }

        if ((this.subscription.isCanceled || this.subscription.isExpired) && this.subscription.isExternalBillingProvider) {
            return 'heroExclamationTriangleSolid';
        }

        return !this.subscription.hasCreditCardAdded ? 'heroCreditCard' : 'heroExclamationTriangleSolid';
    }

    get heading() {
        if (this.subscription.isPendingApproval) {
            return 'CREDIT_CARD_ALERT.PENDING_APPROVAL';
        }

        if ((this.subscription.isCanceled || this.subscription.isExpired) && this.subscription.isExternalBillingProvider) {
            return 'CREDIT_CARD_ALERT.SUBSCRIPTION_CANCELED';
        }

        return !this.subscription.hasCreditCardAdded ? 'CREDIT_CARD_ALERT.ADD_CREDIT_CARD' : 'CREDIT_CARD_ALERT.PAYMENT_FAILED';
    }

    get description() {
        if (this.subscription.isPendingApproval) {
            return this.subscription.isAppleBillingProvider
                ? 'CREDIT_CARD_ALERT.APPLE_PENDING_APPROVAL_DESCRIPTION'
                : 'CREDIT_CARD_ALERT.PENDING_APPROVAL_DESCRIPTION';
        }

        if ((this.subscription.isCanceled || this.subscription.isExpired) && this.subscription.isExternalBillingProvider) {
            // Generic for both Apple and Shopify — the platform-specific resubscribe
            // instructions live on the Billing page (where "View Billing" points).
            return 'CREDIT_CARD_ALERT.SUBSCRIPTION_CANCELED_DESCRIPTION';
        }

        return !this.subscription.hasCreditCardAdded ? 'CREDIT_CARD_ALERT.ADD_CREDIT_CARD_DESCRIPTION' : 'CREDIT_CARD_ALERT.PAYMENT_FAILED_DESCRIPTION';
    }

    handleSubscription() {
        if (this.subscription.isPendingApproval) {
            // Apple IAP purchases happen in-app, no confirmation URL
            if (this.subscription.isAppleBillingProvider) {
                return;
            }

            const url = this.subscription.providerConfirmationUrl;

            if (url) {
                window.open(url, '_top');
            } else {
                // No cached confirmation URL — don't silently no-op. Send the
                // merchant to the Billing page, whose plan cards mint a fresh
                // approval URL for the right plan.
                this.router.navigate(['/account', { section: 'billing' }]);
            }

            return;
        }

        if ((this.subscription.isCanceled || this.subscription.isExpired) && this.subscription.isExternalBillingProvider) {
            // Apple/Shopify subscriptions can't be resubscribed from the admin — send
            // the user to the Billing page, which shows the platform-specific
            // resubscribe CTA (Open Rosie App / Go to Shopify dashboard).
            this.router.navigate(['/account', { section: 'billing' }]);
            return;
        }

        if (!this.subscription.hasCreditCardAdded) {
            this.analytics.trackEvent(AnalyticsEvent.AddCreditCartAlertInitiated, { origin: this.section });
            this.subscription.open.next(null);
        } else {
            this.redirectToBillingPortal();
        }
    }

    private redirectToBillingPortal(): void {
       this.subscription.redirectToBillingPortal();
    }
}

export enum BillingAlertSection {
    Sidebar = 'sidebar',
    Billing = 'billing'
}
